'use client';

import React from 'react';
import { Drawer, IconButton } from '@skin-seoul/design-system';
import CartCloseIcon from './components/CartCloseIcon';
import type { Currency } from '@skin-seoul/service';
import ShoppingBagDrawerContent from './ShoppingBagDrawerContent';
import ShoppingBagSkeleton from './ShoppingBagSkeleton';
import QueryBoundary from '../../utils/react/QueryBoundary';
import { useCartDrawer } from '../../context/CartDrawerContext';

type ShoppingBagDrawerProps = {
  currency: Currency;
  onClickContinueShopping: () => void;
  /**
   * Optional callback triggered when checkout button is clicked
   * Parent can use this for analytics tracking
   */
  onCheckoutClick?: () => void;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  /**
   * Custom Link component to use for navigation.
   * Useful for injecting Next.js Link in webapp or other routing solutions.
   * @default Link from @skin-seoul/design-system
   */
  linkComponent?: React.ElementType;
};

const ShoppingBagDrawer = ({
  currency,
  onClickContinueShopping,
  onCheckoutClick,
  ImageComponent,
  linkComponent,
}: ShoppingBagDrawerProps) => {
  const { isOpen, onSetCartState, onCartClose } = useCartDrawer();

  return (
    <Drawer.Root onOpenChange={(e) => onSetCartState(e.open)} open={isOpen} initialFocusEl={() => null}>
      <Drawer.Backdrop />
      <Drawer.Positioner
        zIndex="skipNav"
        onClick={(e) => {
          //TODO: add this is temporarily fix of close outside at wp
          if (e.target === e.currentTarget) {
            onCartClose();
          }
        }}
      >
        <Drawer.Content
          maxWidth={{
            base: '100%',
            mobile: '450px',
            tablet: '450px',
            desktop: '450px',
          }}
          alignItems="center"
          justifyContent="center"
          backgroundColor="white"
          width="100%"
          tabIndex={isOpen ? undefined : -1}
          aria-hidden={!isOpen}
          //TODO: add this is temporarily fix of close outside at wp
          id="shopping-bag-drawer-content"
        >
          <IconButton
            _hover={{ backgroundColor: 'transparent' }}
            aria-label="Close cart"
            backgroundColor="transparent"
            border="none"
            cursor="pointer"
            position="absolute"
            top="16px"
            left="16px"
            zIndex="banner"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCartClose();
            }}
            tabIndex={isOpen ? 0 : -1}
          >
            <CartCloseIcon />
          </IconButton>
          <QueryBoundary loadingFallback={<ShoppingBagSkeleton />}>
            <ShoppingBagDrawerContent
              currency={currency}
              onClickContinueShopping={onClickContinueShopping}
              onCheckoutClick={onCheckoutClick}
              ImageComponent={ImageComponent}
              linkComponent={linkComponent}
            />
          </QueryBoundary>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
};

export default ShoppingBagDrawer;
